import { useMemo, useState } from 'react'
import { ChevronDown, Languages as LanguagesIcon, Layers, X } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlowButton } from '@/components/ui/GlowButton'
import { InquiryModal } from '@/components/ui/InquiryModal'
import {
  ADDONS,
  MAINTENANCE_ADDON,
  LANGUAGE_OPTIONS,
  LANGUAGE_PRICE_PER_LANG,
  BASE_PRICE,
  computeTier,
} from '@/lib/pricing'
import { AddonRow } from './AddonRow'
import { TierBadge } from './TierBadge'
import { ADDON_ICONS, iconForId } from './icons'

const MaintenanceIcon = ADDON_ICONS.maintenance

/**
 * Main "new website" calculator + bundle-savings sidebar. Logic ported
 * exactly from index.html's `recalc()` + the bundle-panel IIFE (see
 * app/handoff.md §4) — one deliberate simplification: the live site keeps
 * two independent listeners over duplicated data (`ADDONS` here vs. a
 * separate `BUNDLEABLE` array there); here they're the same data, since
 * every bundleable item in `ADDONS` already carries both prices needed for
 * the savings table. Behavior is identical, the duplication wasn't.
 */
export function WebCalculator() {
  const { t } = useLanguage()
  const [activeIds, setActiveIds] = useState<Set<string>>(new Set())
  const [maintenanceOn, setMaintenanceOn] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [languages, setLanguages] = useState<string[]>([])
  const [langSelect, setLangSelect] = useState('')
  const [langCustom, setLangCustom] = useState('')
  const [inquiryOpen, setInquiryOpen] = useState(false)
  const [showAllAddons, setShowAllAddons] = useState(false)

  function toggleAddon(id: string) {
    setActiveIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleLang() {
    const next = !langOpen
    setLangOpen(next)
    if (!next) setLanguages([])
  }

  function addLanguage() {
    const val = langSelect === 'custom' ? langCustom.trim() : langSelect
    if (!val || languages.includes(val)) return
    setLanguages((prev) => [...prev, val])
    setLangCustom('')
    setLangSelect('')
  }

  function removeLanguage(lang: string) {
    setLanguages((prev) => prev.filter((l) => l !== lang))
  }

  const { oneTime, recurring, breakdown, tier, activeBundleable, langTotal } = useMemo(() => {
    let oneTime = BASE_PRICE
    let recurring = 0
    let forcePremium = false
    const breakdown: { name: string; price: number; suffix: string }[] = []
    const activeBundleable = ADDONS.filter((a) => activeIds.has(a.id))

    for (const addon of activeBundleable) {
      oneTime += addon.bundledPrice
      if (addon.forcePremium) forcePremium = true
      breakdown.push({ name: t(addon.nameKey), price: addon.bundledPrice, suffix: 'EUR' })
    }

    const langTotal = languages.length * LANGUAGE_PRICE_PER_LANG
    if (langTotal > 0) {
      oneTime += langTotal
      breakdown.push({ name: `${t('addon-lang-name')} (${languages.join(', ')})`, price: langTotal, suffix: 'EUR' })
    }

    if (maintenanceOn) {
      recurring += MAINTENANCE_ADDON.price
      breakdown.push({ name: t(MAINTENANCE_ADDON.nameKey), price: MAINTENANCE_ADDON.price, suffix: 'EUR/mj' })
    }

    return { oneTime, recurring, breakdown, tier: computeTier(oneTime, forcePremium), activeBundleable, langTotal }
  }, [activeIds, languages, maintenanceOn, t])

  const summaryLines = [
    t('services-title'),
    `${t('tier-basic')}: ${BASE_PRICE} EUR`,
    ...breakdown.map((b) => `+ ${b.name}: ${b.price} ${b.suffix}`),
    `${t('bundle-total')}: ${oneTime} EUR${recurring ? ` + ${recurring} EUR/mj` : ''}`,
  ]

  function reset() {
    setActiveIds(new Set())
    setMaintenanceOn(false)
    setLangOpen(false)
    setLanguages([])
  }

  // Bundle savings — every ADDONS entry is bundleable by construction.
  const bundleOrigTotal = activeBundleable.reduce((sum, a) => sum + a.standalonePrice, 0)
  const bundleBundledTotal = activeBundleable.reduce((sum, a) => sum + a.bundledPrice, 0)
  const bundleSaved = bundleOrigTotal - bundleBundledTotal
  const bundlePct = bundleOrigTotal > 0 ? Math.round((bundleSaved / bundleOrigTotal) * 100) : 0

  return (
    <div className="grid grid-cols-1 gap-gutter lg:grid-cols-12">
      {/* LEFT: calculator */}
      <GlassCard className="relative flex flex-col gap-6 overflow-hidden p-6 lg:col-span-7 md:p-8">
        <div className="absolute left-1/4 top-0 h-px w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="flex flex-col items-start justify-between gap-4 border-b border-white/10 pb-6 md:flex-row md:items-center">
          <div>
            <h3 className="font-serif text-xl font-semibold text-foreground md:text-2xl">{t('calc-config-label')}</h3>
            <p className="mt-1 text-sm text-foreground-muted">{t('calc-pick')}</p>
          </div>
          <TierBadge tier={tier} />
        </div>

        {/* Reverted from 2-col-on-mobile: AddonRow's icon+name+note+price
            all sit in one horizontal row, which doesn't fit a half-width
            mobile column without the name/note text colliding. Single
            column at every breakpoint, matching GraphicDesignPanel's
            (vertically-stacked-card) 2-col treatment which has no such
            problem and stays as-is. */}
        <div className="grid grid-cols-1 gap-3">
          {/* Halfway show-more: first half of ADDONS always visible, the rest
              (plus the language row and maintenance row below) collapsed
              behind a toggle — the full list plus those two extra rows was
              running too long on both mobile and desktop. */}
          {ADDONS.slice(0, Math.ceil(ADDONS.length / 2)).map((addon) => {
            const Icon = iconForId(addon.id)
            return (
              <AddonRow
                key={addon.id}
                active={activeIds.has(addon.id)}
                onToggle={() => toggleAddon(addon.id)}
                icon={<Icon size={16} />}
                name={t(addon.nameKey)}
                note={t(addon.noteKey)}
                price={addon.bundledPrice}
                standalonePrice={addon.standalonePrice}
              />
            )
          })}

          {!showAllAddons && (
            <button
              type="button"
              onClick={() => setShowAllAddons(true)}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/15 py-2.5 text-sm font-semibold text-foreground-muted transition-colors hover:border-accent-fire/50 hover:text-accent-fire"
            >
              {t('calc-show-more')}
              <ChevronDown size={16} />
            </button>
          )}

          {showAllAddons && (
            <>
              {ADDONS.slice(Math.ceil(ADDONS.length / 2)).map((addon) => {
                const Icon = iconForId(addon.id)
                return (
                  <AddonRow
                    key={addon.id}
                    active={activeIds.has(addon.id)}
                    onToggle={() => toggleAddon(addon.id)}
                    icon={<Icon size={16} />}
                    name={t(addon.nameKey)}
                    note={t(addon.noteKey)}
                    price={addon.bundledPrice}
                    standalonePrice={addon.standalonePrice}
                  />
                )
              })}
            </>
          )}

          {/* Language add-on: custom sub-row, not a plain toggle. Always spans
              both mobile columns — it has its own expandable panel and
              doesn't fit a half-width card. Part of the collapsed group. */}
          {showAllAddons && (
          <div
            className={`rounded-lg border transition-all duration-normal ${
              langOpen ? 'glow-ring border-accent-fire/50 bg-accent-fire/10' : 'border-white/5 bg-white/[0.03] hover:border-white/15'
            }`}
          >
            <div
              role="checkbox"
              aria-checked={langOpen}
              tabIndex={0}
              onClick={toggleLang}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault()
                  toggleLang()
                }
              }}
              className="flex cursor-pointer items-start gap-4 p-4"
            >
              <div
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
                  langOpen ? 'border-accent-fire bg-accent-fire/20' : 'border-white/25'
                }`}
              >
                <LanguagesIcon
                  size={12}
                  strokeWidth={3}
                  className={`text-accent-fire transition-transform duration-fast ${langOpen ? 'scale-100' : 'scale-0'}`}
                />
              </div>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-foreground-muted">
                <LanguagesIcon size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground">{t('addon-lang-name')}</div>
                <div className="mt-0.5 text-xs text-foreground-muted">{t('addon-lang-note')}</div>
                <div className="mt-2 text-sm font-bold text-accent-amber-light">+{langTotal} EUR</div>
              </div>
            </div>

            {langOpen && (
              <div className="flex flex-col gap-3 border-t border-white/10 p-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-wrap gap-2">
                  <select
                    value={langSelect}
                    onChange={(e) => setLangSelect(e.target.value)}
                    className="rounded border border-white/15 bg-background px-3 py-2 text-sm text-foreground"
                  >
                    <option value="">{t('lang-pick')}</option>
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <option key={opt.id} value={t(opt.labelKey)}>
                        {t(opt.labelKey)}
                      </option>
                    ))}
                    <option value="custom">{t('lang-custom')}</option>
                  </select>
                  {langSelect === 'custom' && (
                    <input
                      type="text"
                      value={langCustom}
                      onChange={(e) => setLangCustom(e.target.value)}
                      placeholder={t('lang-custom')}
                      className="rounded border border-white/15 bg-background px-3 py-2 text-sm text-foreground"
                    />
                  )}
                  <button
                    type="button"
                    onClick={addLanguage}
                    className="rounded-pill bg-accent-fire/20 px-4 py-2 text-xs font-bold text-accent-fire transition-colors hover:bg-accent-fire/30"
                  >
                    {t('lang-add-btn')} +{LANGUAGE_PRICE_PER_LANG} EUR
                  </button>
                </div>
                {languages.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {languages.map((l) => (
                      <span
                        key={l}
                        className="inline-flex items-center gap-1.5 rounded-pill bg-white/10 px-3 py-1 text-xs text-foreground"
                      >
                        {l}
                        <button
                          type="button"
                          onClick={() => removeLanguage(l)}
                          aria-label={`${t('calc-reset')} ${l}`}
                          className="text-foreground-muted hover:text-accent-fire"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {showAllAddons && (
          <AddonRow
            active={maintenanceOn}
            onToggle={() => setMaintenanceOn((v) => !v)}
            icon={<MaintenanceIcon size={16} />}
            name={t(MAINTENANCE_ADDON.nameKey)}
            note={t(MAINTENANCE_ADDON.noteKey)}
            price={MAINTENANCE_ADDON.price}
            priceSuffix={`EUR ${t('addon-maint-price')}`}
          />
          )}

          {showAllAddons && (
            <button
              type="button"
              onClick={() => setShowAllAddons(false)}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/15 py-2.5 text-sm font-semibold text-foreground-muted transition-colors hover:border-accent-fire/50 hover:text-accent-fire"
            >
              {t('calc-show-less')}
              <ChevronDown size={16} className="rotate-180" />
            </button>
          )}
        </div>

        <div className="mt-2 flex flex-col items-center justify-between gap-6 border-t border-white/10 pt-6 md:flex-row">
          <div className="text-center md:text-left">
            <span className="block font-mono text-xs uppercase tracking-widest text-foreground-muted">
              {t('calc-price-label')}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-3xl font-bold text-accent-amber-light md:text-4xl">
                {oneTime} EUR
              </span>
              {recurring > 0 && <span className="text-sm text-foreground-muted">+ {recurring} EUR/mj</span>}
            </div>
            <p className="mt-1 max-w-xs text-xs text-foreground-subtle">{t('calc-note')}</p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <GlowButton type="button" onClick={() => setInquiryOpen(true)}>
              {t('calc-cta')}
            </GlowButton>
            <button
              type="button"
              onClick={reset}
              className="rounded-pill border border-border-strong px-6 py-2.5 text-sm font-semibold text-foreground-muted transition-colors hover:border-accent-fire hover:text-accent-fire"
            >
              {t('calc-reset')}
            </button>
          </div>
        </div>
      </GlassCard>

      {/* RIGHT: bundle savings */}
      <GlassCard className="flex flex-col gap-5 p-6 lg:col-span-5 md:p-8">
        <div className="flex items-center gap-2">
          <Layers size={20} className="text-accent-amber-light" />
          <h3 className="font-serif text-lg font-semibold text-foreground">{t('bundle-discount-label')}</h3>
          {bundlePct > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-pill bg-accent-cyan/15 px-2.5 py-1 font-mono text-[11px] font-bold text-accent-cyan">
              {bundlePct}%
            </span>
          )}
        </div>

        {activeBundleable.length === 0 ? (
          <p className="text-sm text-foreground-muted">{t('bundle-empty')}</p>
        ) : (
          <>
            <p className="text-sm text-foreground-muted">{t('bundle-title')}</p>
            <div className="flex flex-col divide-y divide-white/5 rounded-lg border border-white/10 bg-white/[0.02]">
              {activeBundleable.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 px-4 py-2.5 text-xs">
                  <span className="text-foreground-muted">{t(a.nameKey)}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-foreground-subtle line-through">{a.standalonePrice}</span>
                    <span className="font-semibold text-accent-amber-light">{a.bundledPrice} EUR</span>
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between gap-2 bg-white/[0.03] px-4 py-3">
                <span className="text-xs font-bold uppercase tracking-wide text-foreground">{t('bundle-total')}</span>
                <span className="flex items-center gap-2 text-sm">
                  <span className="text-foreground-subtle line-through">{bundleOrigTotal} EUR</span>
                  <span className="font-bold text-accent-amber-light">{bundleBundledTotal} EUR</span>
                </span>
              </div>
            </div>
            <p className="text-xs text-foreground-subtle">
              {t('bundle-th-saved')}: <span className="font-semibold text-accent-cyan">-{bundleSaved} EUR</span>
            </p>
          </>
        )}
      </GlassCard>

      <InquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} summary={summaryLines} />
    </div>
  )
}
